import { makeStyles } from "@material-ui/core";
import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import { createPortal } from "react-dom";
import { Select } from "antd";
import { REPORT_FONT, useReportFonts } from "../reportFonts";
import BackButton from "../BackButton";
import { goToReportsCatalog } from "../reportNav";
import { useHistory, useParams } from "react-router-dom";
import {
  FILTERS_CONFIG,
  initFilterValues,
  isFilterActive,
  getDisplayValue,
  getDefaultValue,
  getActiveFilterCount,
  areVisibleFilterValuesEqual,
  HorizontalDateRangeFilter,
  FilterApplyButton,
} from "./GlobalFilterPanel";
import { useExpandedAppliedFilters } from "./useExpandedAppliedFilters";
import {
  applyCascadeDraftChange,
  pruneInvalidCascadeSelections,
  getFilterFieldOptions,
  toFilterSelectOptions,
  FILTER_SELECT_SEARCH_PROPS,
  GlobalMultiSelectFilter,
  resolvePopEndDateForApi,
} from "./filterRelationshipUtils";
import {
  hasActiveNumFilters,
  hasActiveTextFilters,
  hasAnyTableFilters,
  sumRows,
} from "./tableTotalUtils";
import {
  usePlByProgramStyles,
  PlPageHeader,
} from "./pl-by-program/plByProgramTheme";
import {
  calcFilterPos,
  calcOpDropdownPos,
  FILTER_PANEL_WIDTH_NUMERIC,
} from "./waterfall-eac/filterPopoverUtils";
import {
  toggleSortKey,
  compareBySortKeys,
  createSortThClick,
  WaterfallThLabel,
} from "./waterfall-eac/waterfallTableUtils";
import {
  NumericFilterContent,
  TextFilterContent,
  OpDropdownContent,
  fpBase,
} from "./waterfall-eac/tableColumnFilterShared";
import {
  getVariancesByContractAPI,
  getVariancesByContractL2API,
  downloadVariancesByContractAPI,
  downloadVariancesByContractL2API,
  getVersionsAPI,
} from "../../../api/reports-api";
import Loading from "../../../common-components/Loading";
import {
  TABLE_FONT,
  TH_BG,
  TH_TEXT,
  TH_BORDER,
  TH_BG_BAND,
} from "../reportTableConfig";


const TAB_ACTIVE = "#4F46E5";
const BORDER = "#E5E7EB";
const FONT = TABLE_FONT;

const MONTH_OPTS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
].map((m) => ({ value: m, label: m }));

/** Last actual month for the selected fiscal year (matches Actuals through header logic). */
function defaultMonthAbbrForYear(yearFilter) {
  const currentCalendarYear = new Date().getFullYear();
  const selected =
    yearFilter && String(yearFilter) !== "All"
      ? Number(yearFilter)
      : currentCalendarYear;
  if (Number.isNaN(selected) || selected > currentCalendarYear) {
    return MONTH_OPTS[0].value;
  }
  if (selected < currentCalendarYear) return "Dec";
  const prev = new Date();
  prev.setMonth(prev.getMonth() - 1);
  return MONTH_OPTS[prev.getMonth()]?.value ?? "Jan";
}

function monthAbbrFromActualThrough(actualThroughMonth) {
  const m = Number(actualThroughMonth);
  if (m >= 1 && m <= 12) return MONTH_OPTS[m - 1]?.value;
  return null;
}

const VALUE_OPTS = [
  { value: "contractRevenue", label: "Revenue" },
  { value: "grossMargin", label: "Gross Margin" },
];

function resolveDefaultVersions(list) {
  const from = list.find((v) => v.key === "liveEac")?.key ?? list[0]?.key ?? "";
  const to = list.find((v) => v.key !== "liveEac")?.key ?? "";
  return { from, to };
}

/** Global filters — matches Power BI Variances by Contract filter pane */
const TAB_FILTERS_KEYS = [
  "year",
  "monthName",
  "quarter",
  "orgId",
  "contractType",
  "contractId",
  "clinLvl2",
  "businessType",
  "awardType",
  "competitionType",
  "bu",
  "popEndDate",
];

/* Two tabs, identical UI/filters/table underneath — "L2" duplicates the same
   SummaryTable with no new API wiring, just a different displayed title. */
const TABS = [
  { key: "variances_by_contract",    label: "Variances by Contract" },
  { key: "variances_by_contract_l2", label: "Variances by Contract L2" },
];

function filtersForVisibleKeys(visibleKeys) {
  const byKey = Object.fromEntries(FILTERS_CONFIG.map((f) => [f.key, f]));
  return (visibleKeys || []).map((k) => byKey[k]).filter(Boolean);
}

const VARIANCES_API_ARRAY_KEYS = [
  "contractId", "projectId", "bu", "orgId", "contractName", "customerName",
  "sector", "competitionType", "awardType", "businessType",
];

function buildVariancesByContractFilters(globalFilters, selectedMonth) {
  if (!globalFilters) return {};
  const { quarter, contractType, CLIN, monthName: globalMonth, popEndDate, ...rest } =
    globalFilters;
  const f = {};
  if (rest.year && String(rest.year) !== "All") f.year = String(rest.year);
  // Union the page's MTD-month dropdown with whatever the global filter bar's
  // Month selection is — previously the dropdown always won and the filter
  // bar's choice was silently dropped whenever it had a default value.
  const monthSet = new Set();
  if (Array.isArray(globalMonth)) globalMonth.forEach((m) => monthSet.add(m));
  if (selectedMonth) monthSet.add(selectedMonth);
  if (monthSet.size > 0) f.monthName = [...monthSet];
  if (Array.isArray(quarter) && quarter.length > 0) f.quarter = quarter;
  if (Array.isArray(contractType) && contractType.length > 0) f.contractType = contractType;
  if (Array.isArray(CLIN) && CLIN.length > 0) f.CLIN = CLIN;
  VARIANCES_API_ARRAY_KEYS.forEach((key) => {
    if (Array.isArray(rest[key]) && rest[key].length > 0) f[key] = rest[key];
  });
  const popRange = resolvePopEndDateForApi(popEndDate);
  if (popRange) f.popEndDate = popRange;
  return f;
}

function buildScrollCols(fromName, toName, mtdHeader, qtdHeader) {
  const groups = buildGroups(mtdHeader, qtdHeader);
  const subs = [
    { suffix: "From", label: fromName },
    { suffix: "To", label: toName },
    { suffix: "Variance", label: "Variance", isVariance: true },
  ];
  return groups.flatMap((g) =>
    subs.map((s) => ({
      key: `${g.key}${s.suffix}`,
      groupKey: g.key,
      groupLabel: g.label,
      label: s.label,
      isText: false,
      isVariance: !!s.isVariance,
      w: 130,
    })),
  );
}

function buildGroups(mtdHeader, qtdHeader) {
  return [
    { key: "mtd", label: mtdHeader },
    ...(qtdHeader ? [{ key: "qtd", label: qtdHeader }] : []),
    { key: "ytd", label: "YTD" },
    { key: "fy", label: "Full Year" },
  ];
}

const DEFAULT_SCROLL_COLS = buildScrollCols("From", "To", "MTD");
const DEFAULT_GROUPS = buildGroups("MTD");

/* ── Table structure ── */
const BASE_FIXED_COLS = [
  { key: "contractId", label: "Contract ID", isText: true, w: 140 },
  { key: "contractName", label: "Contract Name", isText: true, w: 220 },
];
const STICKY_W = BASE_FIXED_COLS.reduce((s, c) => s + c.w, 0);

/* ── Helpers ── */
function isOnlyDefaultYear(filterValues, vKeys) {
  const keys = (vKeys || FILTERS_CONFIG.map((f) => f.key)).filter(
    (k) => k !== "year",
  );
  return (
    !keys.some((k) => {
      const f = FILTERS_CONFIG.find((fc) => fc.key === k);
      return f && isFilterActive(f, filterValues[k]);
    }) && String(filterValues.year) === String(new Date().getFullYear())
  );
}

function fmtMoney(v, showZero) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  if (!showZero && n === 0) return "";
  const abs = Math.abs(n).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return n < 0 ? `($${abs})` : `$${abs}`;
}

function fmtPct(v) {
  if (v === null || v === undefined) return "—";
  const n = Number(v);
  const s = `${(Math.abs(n) * 100).toFixed(1)}%`;
  return n < 0 ? `(${s})` : s;
}

function formatCell(value, isPct, isVariance, showZero) {
  if (value === null || value === undefined) return "—";
  if (isPct) return fmtPct(value);
  return fmtMoney(value, showZero || isVariance);
}

function fmtN(v) {
  return fmtMoney(v, false);
}

function fmtNTotal(v, isPct) {
  if (isPct) return fmtPct(v);
  return fmtMoney(v, true);
}

function applyNumFilter(rawVal, filter) {
  if (!filter || filter.value === "" || filter.value === undefined) return true;
  const v = rawVal == null ? 0 : Number(rawVal);
  const fv = Number(filter.value);
  switch (filter.op || "gt") {
    case "gt":
      return v > fv;
    case "gte":
      return v >= fv;
    case "lt":
      return v < fv;
    case "lte":
      return v <= fv;
    case "eq":
      return v === fv;
    case "neq":
      return v !== fv;
    default:
      return true;
  }
}

/* ══════════════════════════════════════════════════════════════ */
/*  Styles                                                        */
/* ══════════════════════════════════════════════════════════════ */
const useStyles = makeStyles(() => ({
  page: {
    fontFamily: FONT,
    backgroundColor: "#F4F5F7",
    display: "flex",
    flexDirection: "column",
    minHeight: "calc(100vh - 80px)",
  },
  topBar: {
    background: "#fff",
    padding: "12px 24px",
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexShrink: 0,
  },
  crumb: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 14,
    color: "#9CA3AF",
    minWidth: 0,
  },
  crumbLink: {
    cursor: "pointer",
    color: "#9CA3AF",
    "&:hover": { color: "#6B7280" },
  },
  crumbSep: { color: "#D1D5DB", userSelect: "none" },
  crumbCur: { color: "#111827", fontWeight: 500 },
  topActions: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  actionBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6,
    height: 32,
    padding: "0 12px",
    borderRadius: 6,
    border: "1px solid #E5E7EB",
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s",
    "&:hover": { background: "#F9FAFB", borderColor: "#D1D5DB" },
  },
  actionBtnActive: {
    borderColor: TAB_ACTIVE,
    background: TAB_ACTIVE,
    color: "#fff",
    "&:hover": { background: "#4338CA", borderColor: "#4338CA" },
  },
  actionBtnIconOnly: {
    width: 32,
    minWidth: 32,
    padding: 0,
    justifyContent: "center",
  },
  filterBadge: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 18,
    height: 18,
    padding: "0 5px",
    borderRadius: 9,
    background: "rgba(255,255,255,0.25)",
    color: "#fff",
    fontSize: 11,
    fontWeight: 700,
    lineHeight: 1,
  },
  tabBar: {
    background: "#fff",
    borderBottom: "1px solid #E5E7EB",
    display: "flex",
    flexWrap: "wrap",
    flexShrink: 0,
    padding: "0 24px",
    gap: "4px 0",
  },
  tab: {
    padding: "12px 16px",
    fontSize: 14,
    fontWeight: 500,
    color: "#374151",
    cursor: "pointer",
    whiteSpace: "nowrap",
    border: "none",
    borderBottom: "2px solid transparent",
    background: "none",
    marginBottom: -1,
    transition: "color 0.15s, border-color 0.15s",
    "&:hover": { color: TAB_ACTIVE },
  },
  tabActive: {
    color: TAB_ACTIVE,
    fontWeight: 700,
    borderBottom: `2px solid ${TAB_ACTIVE}`,
  },
  headerContainer: { margin: "20px 0 0", padding: "0 16px", flexShrink: 0 },
  body: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  content: { flex: 1, padding: "16px", overflowY: "auto", minWidth: 0 },

  filterRow: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    marginBottom: 12,
    gap: 10,
  },
  filterGroup: {
    display: "inline-flex",
    alignItems: "stretch",
    height: 36,
    borderRadius: 28,
    border: "1px solid #E2E8F0",
    background: "#FFFFFF",
    overflow: "hidden",
    flexShrink: 0,
    boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
    transition: "border-color 0.15s, box-shadow 0.15s",
    "&:hover": {
      borderColor: "#C7D2FE",
      boxShadow: "0 3px 10px rgba(74,85,162,0.12)",
    },
    "&:focus-within": {
      borderColor: "#A5B4FC",
      boxShadow: "0 0 0 3px rgba(240,242,255,0.9)",
    },
  },
  filterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#4A55A2",
    padding: "0 14px",
    background: "#F0F2FF",
    height: "100%",
    display: "flex",
    alignItems: "center",
    letterSpacing: "0.12em",
    textTransform: "uppercase",
    userSelect: "none",
    fontFamily: FONT,
    flexShrink: 0,
    whiteSpace: "nowrap",
  },
  filterSelect: {
    border: "none",
    outline: "none",
    backgroundColor: "#FFFFFF",
    fontSize: 13,
    fontWeight: 400,
    color: "#111111",
    padding: "0 28px 0 14px",
    cursor: "pointer",
    appearance: "none",
    WebkitAppearance: "none",
    minWidth: 120,
    height: "100%",
    fontFamily: FONT,
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%2364748B' stroke-width='1.25' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E")`,
    backgroundRepeat: "no-repeat",
    backgroundPosition: "right 12px center",
  },

  /* ── Horizontal filter bar ── */
  hFilterBar: {
    position: "relative",
    background: "#d6dbf8",
    borderTop: "2px solid #191b8c",
    borderBottom: `1px solid ${BORDER}`,
    padding: "12px 52px 12px 24px",
    display: "flex",
    alignItems: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    flexShrink: 0,
  },
  hFilterGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
    flexShrink: 0,
    background: "#fff",
    borderRadius: 8,
    padding: "6px 10px 8px",
    minWidth: 120,
  },
  hFilterLabel: {
    fontSize: 10,
    fontWeight: 700,
    color: "#191b8c",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    whiteSpace: "nowrap",
  },
  hFilterInput: {
    height: 26,
    padding: "0 6px",
    border: "none",
    borderRadius: 4,
    fontSize: 12,
    fontFamily: FONT,
    color: "#111827",
    outline: "none",
    minWidth: 110,
    background: "transparent",
    "&:focus": { outline: `1px solid ${TAB_ACTIVE}`, borderRadius: 4 },
  },
  hFilterActions: {
    display: "flex",
    alignItems: "flex-end",
    gap: 8,
    marginLeft: "auto",
    flexShrink: 0,
  },
  hFilterApply: {
    height: 34,
    padding: "0 16px",
    borderRadius: 6,
    border: "none",
    background: TAB_ACTIVE,
    color: "#fff",
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: FONT,
    whiteSpace: "nowrap",
    "&:hover": { background: "#4338CA" },
  "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  "&:disabled:hover": { background: "inherit" },
  },
  hFilterClearAll: {
    alignSelf: "flex-end",
    height: 34,
    padding: "0 12px",
    borderRadius: 6,
    border: `1px solid ${BORDER}`,
    background: "#fff",
    color: "#374151",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    flexShrink: 0,
    whiteSpace: "nowrap",
    fontFamily: FONT,
    "&:hover": { color: TAB_ACTIVE, borderColor: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  hFilterClose: {
    position: "absolute",
    top: 10,
    right: 10,
    width: 28,
    height: 28,
    borderRadius: "50%",
    border: "2px solid #191b8c",
    background: TH_BG_BAND,
    color: "#fff",
    fontSize: 13,
    fontWeight: 700,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontFamily: FONT,
    "&:hover": { background: "#111827" },
  },

  /* ── Active filter chips ── */
  chipsBar: {
    display: "flex",
    alignItems: "center",
    gap: 6,
    padding: "8px 16px",
    background: "#F8F9FF",
    borderBottom: `1px solid ${BORDER}`,
    flexWrap: "wrap",
    flexShrink: 0,
  },
  chip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    height: 26,
    padding: "0 10px",
    borderRadius: 20,
    background: TH_BG_BAND,
    border: `1px solid ${TH_BG_BAND}`,
    fontSize: 11,
    fontWeight: 500,
    color: "#F1F5F9",
    whiteSpace: "nowrap",
    flexShrink: 0,
  },
  chipLabel: { color: "#94A3B8", fontWeight: 400, marginRight: 2 },
  chipClear: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "none",
    border: "none",
    cursor: "pointer",
    color: "#94A3B8",
    fontSize: 10,
    padding: 0,
    marginLeft: 2,
    "&:hover": { background: "rgba(255,255,255,0.15)", color: "#fff" },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
  chipsClearAll: {
    fontSize: 11,
    fontWeight: 600,
    color: "#6B7280",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "0 6px",
    marginLeft: 4,
    whiteSpace: "nowrap",
    flexShrink: 0,
    "&:hover": { color: TAB_ACTIVE },
    "&:disabled": { opacity: 0.38, cursor: "not-allowed" },
  },
}));

/* ══════════════════════════════════════════════════════════════ */
/*  Icons                                                         */
/* ══════════════════════════════════════════════════════════════ */
function FilterIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="4" y1="6" x2="20" y2="6" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="10" y1="18" x2="14" y2="18" />
    </svg>
  );
}
function DownloadIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
function FilterFunnelIcon() {
  return (
    <svg
      width="10"
      height="10"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
    </svg>
  );
}
function ColumnsIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect x="3" y="3" width="7" height="18" rx="1" />
      <rect x="14" y="3" width="7" height="18" rx="1" />
    </svg>
  );
}

/* ── Horizontal filter bar ─────────────────────────────────── */
function HorizontalFilterBar({
  visibleKeys,
  draft,
  dropdownOptions,
  onChange,
  onClearAll,
  onApply,
  onClose,
  classes,
  clearAllDisabled,
  applyDisabled,
}) {
  const filters = visibleKeys
    ? filtersForVisibleKeys(visibleKeys)
    : FILTERS_CONFIG;
  const selectStyle = { minWidth: 110, maxWidth: 180, fontSize: 12 };
  return (
    <div className={classes.hFilterBar}>
      {filters.map((f) => {
        const opts = toFilterSelectOptions(
          f.key,
          getFilterFieldOptions(f, dropdownOptions),
        );
        const val = draft[f.key];
        return (
          <div key={f.key} className={classes.hFilterGroup}>
            <span className={classes.hFilterLabel}>{f.label}</span>
            {f.type === "select" && (
              <Select
                size="small"
                style={selectStyle}
                value={val || (f.key === "year" ? opts[0]?.value : "All")}
                onChange={(v) =>
                  onChange(f.key, v === "All" ? f.defaultValue : v)
                }
                options={
                  f.key === "year"
                    ? opts
                    : [{ value: "All", label: "All" }, ...opts]
                }
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "multiselect" && (
              <GlobalMultiSelectFilter
                size="small"
                style={selectStyle}
                value={val}
                options={opts}
                onChange={(v) => onChange(f.key, v)}
                {...FILTER_SELECT_SEARCH_PROPS}
                popupMatchSelectWidth={false}
              />
            )}
            {f.type === "text" && (
              <input
                className={classes.hFilterInput}
                value={val || ""}
                placeholder="All"
                onChange={(e) => onChange(f.key, e.target.value)}
              />
            )}
            {f.type === "daterange" && (
              <HorizontalDateRangeFilter
                value={val}
                onChange={(next) => onChange(f.key, next)}
              />
            )}
          </div>
        );
      })}
      <div className={classes.hFilterActions}>
        <button
          className={classes.hFilterClearAll}
          onClick={onClearAll}
          disabled={clearAllDisabled}
        >
          Clear All
        </button>
        <FilterApplyButton
          draft={draft}
          onApply={onApply}
          className={classes.hFilterApply}
          disabled={applyDisabled}
        >
          Apply
        </FilterApplyButton>
      </div>
      <button
        className={classes.hFilterClose}
        onClick={onClose}
        title="Close Filters"
      >
        ✕
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Table style tokens (colors from reportTableConfig)            */
/* ══════════════════════════════════════════════════════════════ */
const TH_SHADOW = `inset -1px 0 0 ${TH_BORDER}, inset 0 -1px 0 ${TH_BORDER}`;

const thGroupStyle = {
  background: TH_BG,
  color: TH_TEXT,
  height: 32,
  fontWeight: 700,
  fontSize: 11,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: "center",
  whiteSpace: "nowrap",
  border: "none",
  boxShadow: TH_SHADOW,
  verticalAlign: "middle",
  padding: "0 10px",
};

const thFixedBlankStyle = {
  background: TH_BG,
  height: 32,
  border: "none",
  boxShadow: TH_SHADOW,
  position: "sticky",
  left: 0,
  zIndex: 5,
  minWidth: STICKY_W,
  width: STICKY_W,
};

const thColStyle = (isNumeric, left, isSticky) => ({
  background: TH_BG,
  color: isNumeric ? "rgba(255,255,255,.75)" : TH_TEXT,
  padding: "0 10px",
  height: 34,
  fontWeight: 700,
  fontSize: 10,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  textAlign: isNumeric ? "right" : "left",
  whiteSpace: "nowrap",
  border: "none",
  boxShadow: TH_SHADOW,
  verticalAlign: "middle",
  ...(isSticky
    ? { position: "sticky", left, zIndex: 4 }
    : { position: "sticky", top: 0, zIndex: 2 }),
});

const tdStyle = (bg, isNumeric, left, isSticky, isNeg) => ({
  padding: "7px 10px",
  background: bg,
  color: isNeg ? "#DC2626" : "#334155",
  borderBottom: "1px solid #E2E8F0",
  fontSize: 12,
  whiteSpace: "nowrap",
  overflow: "hidden",
  verticalAlign: "middle",
  textAlign: isNumeric ? "right" : "left",
  fontVariantNumeric: isNumeric ? "tabular-nums" : undefined,
  ...(isSticky ? { position: "sticky", left, zIndex: 1 } : {}),
});

const tdTotalStyle = (isNumeric, left, isSticky, isNeg) => ({
  padding: "7px 10px",
  background: "#F0FFF4",
  color: isNeg ? "#DC2626" : "#111827",
  fontWeight: 700,
  borderBottom: "1px solid #CBD5E1",
  fontSize: 12,
  whiteSpace: "nowrap",
  textAlign: isNumeric ? "right" : "left",
  fontVariantNumeric: isNumeric ? "tabular-nums" : undefined,
  verticalAlign: "middle",
  ...(isSticky ? { position: "sticky", left, zIndex: 1 } : {}),
});

const filterIconBtn = (active) => ({
  flexShrink: 0,
  width: 16,
  height: 16,
  background: active ? "rgba(165,180,252,0.18)" : "transparent",
  cursor: "pointer",
  padding: 0,
  border: "none",
  borderRadius: 3,
  color: active ? "#A5B4FC" : "rgba(255,255,255,0.5)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  verticalAlign: "middle",
});

const filterPanelStyle = {
  position: "fixed",
  zIndex: 99,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: "10px 12px",
  minWidth: 190,
  fontFamily: FONT,
};
const filterWrap = {
  display: "flex",
  alignItems: "center",
  gap: 3,
  height: 26,
};
const filterTextInput = {
  flex: 1,
  height: 26,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  fontSize: 11,
  padding: "0 7px",
  outline: "none",
  fontFamily: FONT,
  background: "#fff",
  color: "#111827",
};
const filterNumInput = {
  flex: 1,
  height: 26,
  border: "1.5px solid #CBD5E1",
  borderRadius: 5,
  fontSize: 11,
  padding: "0 5px",
  outline: "none",
  fontFamily: FONT,
  background: "#fff",
  color: "#111827",
  textAlign: "right",
};
const opBtn = (active) => ({
  flexShrink: 0,
  width: 22,
  height: 26,
  border: `1.5px solid ${active ? "#4F46E5" : "#CBD5E1"}`,
  borderRadius: 5,
  background: active ? "#4F46E5" : "#fff",
  fontSize: 11,
  fontWeight: 700,
  color: active ? "#fff" : "#4F46E5",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
  fontFamily: FONT,
});
const clearBtn = {
  flexShrink: 0,
  width: 18,
  height: 18,
  border: "none",
  borderRadius: "50%",
  background: "#E2E8F0",
  fontSize: 9,
  color: "#64748B",
  cursor: "pointer",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 0,
};
const opDropdownStyle = {
  position: "fixed",
  zIndex: 99,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 8,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  minWidth: 190,
  overflow: "hidden",
  fontFamily: FONT,
};

const OP_LABELS = { gt: ">", gte: "≥", lt: "<", lte: "≤", eq: "=", neq: "≠" };
const OP_ENTRIES = [
  { key: "gt", label: "> Greater than" },
  { key: "gte", label: "≥ Greater than or equal" },
  { key: "lt", label: "< Less than" },
  { key: "lte", label: "≤ Less than or equal" },
  { key: "eq", label: "= Equals" },
  { key: "neq", label: "≠ Not equal" },
];

/* ── Pagination styles ── */
const paginationBar = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "10px 20px",
  borderTop: "1px solid #E2E8F0",
  background: "#FAFAFA",
  flexShrink: 0,
  gap: 12,
  flexWrap: "wrap",
};
const paginationInfo = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const paginationRight = { display: "flex", alignItems: "center", gap: 8 };
const paginationControls = { display: "flex", alignItems: "center", gap: 3 };
const pageSizeLabel = {
  fontSize: 12,
  color: "#6B7280",
  fontFamily: FONT,
  whiteSpace: "nowrap",
};
const pageSizeSelect = {
  height: 28,
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  fontSize: 12,
  color: "#374151",
  padding: "0 6px",
  background: "#fff",
  cursor: "pointer",
  fontFamily: FONT,
};
const pgBtn = (disabled) => ({
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: "1px solid #E2E8F0",
  borderRadius: 6,
  background: "#fff",
  color: disabled ? "#94A3B8" : "#374151",
  fontSize: 12,
  fontWeight: 500,
  cursor: disabled ? "default" : "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
  opacity: disabled ? 0.35 : 1,
});
const pgBtnActive = {
  minWidth: 30,
  height: 28,
  padding: "0 6px",
  border: `1px solid ${TH_BG_BAND}`,
  borderRadius: 6,
  background: TH_BG_BAND,
  color: "#fff",
  fontSize: 12,
  fontWeight: 600,
  cursor: "pointer",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: FONT,
};
const pgDots = {
  fontSize: 12,
  color: "#6B7280",
  padding: "0 4px",
  userSelect: "none",
};

const CARD = {
  background: "#fff",
  borderRadius: 12,
  border: "1px solid #EBEBEB",
  boxShadow: "0 1px 2px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.03)",
  overflow: "visible",
  display: "flex",
  flexDirection: "column",
  marginBottom: 20,
};
const HEAD = {
  padding: "16px 20px 12px",
  borderBottom: "1px solid #E2E8F0",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 12,
  position: "relative",
  zIndex: 20,
  overflow: "visible",
  flexShrink: 0,
  background: "#fff",
  borderTopLeftRadius: 12,
  borderTopRightRadius: 12,
};
const CARD_BODY = {
  overflow: "hidden",
  borderBottomLeftRadius: 12,
  borderBottomRightRadius: 12,
};
const COL_PICKER_DROP = {
  position: "absolute",
  top: "calc(100% + 4px)",
  right: 0,
  background: "#fff",
  border: "1px solid #E2E8F0",
  borderRadius: 10,
  boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
  padding: "8px 0",
  minWidth: 200,
  zIndex: 1200,
  maxHeight: 420,
  overflowY: "auto",
  fontFamily: FONT,
};
const TITLE_S = {
  fontSize: 14,
  fontWeight: 600,
  color: "#111827",
  letterSpacing: "-0.01em",
  fontFamily: FONT,
  margin: 0,
};

/* ══════════════════════════════════════════════════════════════ */
/*  Inner table component                                         */
/* ══════════════════════════════════════════════════════════════ */
function SummaryTable({ filters, tableParams, prerequisitesReady, onMeta, title = "Variances by Contract", apiFn = getVariancesByContractAPI, isL2 = false }) {
  const FIXED_COLS = useMemo(
    () =>
      isL2
        ? [
            { key: "contractId", label: "CLIN", isText: true, w: 140 },
            { key: "contractName", label: "CLIN NAME", isText: true, w: 220 },
          ]
        : BASE_FIXED_COLS,
    [isL2],
  );
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [loading, setLoading] = useState(false);
  const [scrollCols, setScrollCols] = useState(DEFAULT_SCROLL_COLS);
  const [groups, setGroups] = useState(DEFAULT_GROUPS);
  const [isPct, setIsPct] = useState(false);

  const apiFilterKey = useMemo(
    () =>
      JSON.stringify({
        filters: buildVariancesByContractFilters(
          filters,
          tableParams?.month,
        ),
        params: {
          version: tableParams?.version,
          toVersion: tableParams?.toVersion,
          value: tableParams?.value,
        },
      }),
    [filters, tableParams],
  );

  useEffect(() => {
    if (!prerequisitesReady) return;
    setLoading(true);
    apiFn(
      buildVariancesByContractFilters(filters, tableParams?.month),
      {
        version: tableParams.version,
        toVersion: tableParams.toVersion,
        value: tableParams.value,
      },
    )
      .then((res) => {
        if (res?.status && Number(res.status) !== 200) {
          setRows([]);
          setTotals(null);
          return;
        }
        const data = res?.data ?? res;
        const fromName = data?.fromVersion?.name ?? "From";
        const toName = data?.toVersion?.name ?? "To";
        const selMonth = Number(data?.selectedMonth) || 1;
        const mtdHeader = `MTD — ${MONTH_OPTS[selMonth - 1]?.label ?? "Jan"}`;
        const qtdHeader = `QTD — Q${Math.ceil(selMonth / 3)}`;
        setScrollCols(buildScrollCols(fromName, toName, mtdHeader, qtdHeader));
        setGroups(buildGroups(mtdHeader, qtdHeader));
        setIsPct(!!data?.isPct);
        setRows(Array.isArray(data?.rows) ? data.rows : []);
        setTotals(data?.totals ?? null);
        onMeta?.({
          fiscalYear: data?.fiscalYear,
          actualThroughMonth: data?.actualThroughMonth,
          fromVersion: data?.fromVersion,
          toVersion: data?.toVersion,
        });
      })
      .catch(() => {
        setRows([]);
        setTotals(null);
      })
      .finally(() => setLoading(false));
  }, [apiFilterKey, prerequisitesReady, apiFn]); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Show/hide columns ── */
  const [hiddenCols, setHiddenCols] = useState(new Set());
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const colPickerRef = useRef(null);

  /* ── Table filters ── */
  const [textFilters, setTextFilters] = useState({});
  const [numFilters, setNumFilters] = useState({});
  const [openFilterKey, setOpenFilterKey] = useState(null);
  const [filterPanelPos, setFilterPanelPos] = useState({ top: 0, left: 0 });
  const [openOpKey, setOpenOpKey] = useState(null);
  const [opDropdownPos, setOpDropdownPos] = useState({ top: 0, left: 0 });
  const filterBtnRefs = useRef({});
  const opBtnRefs = useRef({});
  const openFilterKeyRef = useRef(null);
  const openOpKeyRef = useRef(null);

  /* ── Pagination + sort ── */
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortKeys, setSortKeys] = useState([]);

  useEffect(() => {
    openFilterKeyRef.current = openFilterKey;
  }, [openFilterKey]);
  useEffect(() => {
    openOpKeyRef.current = openOpKey;
  }, [openOpKey]);

  /* ── Derived visible cols ── */
  const visScrollCols = useMemo(
    () => scrollCols.filter((c) => !hiddenCols.has(c.key)),
    [scrollCols, hiddenCols],
  );
  const visGroups = useMemo(
    () => groups.filter((g) => visScrollCols.some((c) => c.groupKey === g.key)),
    [groups, visScrollCols],
  );

  /* ── Reposition panels on scroll ── */
  useEffect(() => {
    const reposition = () => {
      const fk = openFilterKeyRef.current;
      if (fk) {
        const btn = filterBtnRefs.current[fk];
        if (btn)
          setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      }
      const ok = openOpKeyRef.current;
      if (ok) {
        const ob = opBtnRefs.current[ok];
        if (ob) setOpDropdownPos(calcOpDropdownPos(ob));
      }
    };
    window.addEventListener("scroll", reposition, true);
    return () => window.removeEventListener("scroll", reposition, true);
  }, []);

  /* ── Close panels on outside click ── */
  useEffect(() => {
    if (!openFilterKey && !colPickerOpen) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-filter-panel]") &&
        !e.target.closest("[data-filter-btn]") &&
        !e.target.closest("[data-op-dropdown]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenFilterKey(null);
      if (colPickerRef.current && !colPickerRef.current.contains(e.target))
        setColPickerOpen(false);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openFilterKey, colPickerOpen]);

  useEffect(() => {
    if (!openOpKey) return;
    const h = (e) => {
      if (
        !e.target.closest("[data-op-dropdown]") &&
        !e.target.closest("[data-op-dd]") &&
        !e.target.closest("[data-op-btn]")
      )
        setOpenOpKey(null);
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [openOpKey]);

  useEffect(() => {
    setPage(1);
  }, [textFilters, numFilters, apiFilterKey]);

  /* ── Filter helpers ── */
  const isTextActive = (key) => !!(textFilters[key] && textFilters[key].trim());
  const isNumActive = (key) => {
    const f = numFilters[key];
    return !!(f && f.value !== "" && f.value !== undefined);
  };
  const isColActive = (col) =>
    col.isText ? isTextActive(col.key) : isNumActive(col.key);

  const toggleFilter = useCallback(
    (key) => {
      if (openFilterKey === key) {
        setOpenFilterKey(null);
        return;
      }
      const btn = filterBtnRefs.current[key];
      if (btn)
        setFilterPanelPos(calcFilterPos(btn, FILTER_PANEL_WIDTH_NUMERIC));
      setOpenFilterKey(key);
      setOpenOpKey(null);
    },
    [openFilterKey],
  ); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleOpDropdown = useCallback(
    (colKey) => {
      if (openOpKey === colKey) {
        setOpenOpKey(null);
        return;
      }
      const btn = opBtnRefs.current[colKey];
      if (btn) setOpDropdownPos(calcOpDropdownPos(btn));
      setOpenOpKey(colKey);
    },
    [openOpKey],
  );

  const selectOp = (colKey, opKey) => {
    setNumFilters((prev) => ({
      ...prev,
      [colKey]: { ...prev[colKey], op: opKey },
    }));
    setOpenOpKey(null);
  };

  const updateNumFilter = (colKey, field, value) =>
    setNumFilters((prev) => ({
      ...prev,
      [colKey]: { op: "gt", ...prev[colKey], [field]: value },
    }));

  const clearFilter = (key) => {
    const allCols = [...FIXED_COLS, ...scrollCols];
    const col = allCols.find((c) => c.key === key);
    if (col?.isText)
      setTextFilters((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
    else
      setNumFilters((prev) => {
        const n = { ...prev };
        delete n[key];
        return n;
      });
  };

  const getColLabel = (key) => {
    const allCols = [...FIXED_COLS, ...scrollCols];
    return allCols.find((c) => c.key === key)?.label || key;
  };

  const renderFilterContent = (key) => {
    const allCols = [...FIXED_COLS, ...scrollCols];
    const col = allCols.find((c) => c.key === key);
    if (col?.isText) {
      const val = textFilters[key] || "";
      return (
        <TextFilterContent
          label={getColLabel(key)}
          value={val}
          onChange={(v) => setTextFilters((p) => ({ ...p, [key]: v }))}
          onClear={() => clearFilter(key)}
        />
      );
    }
    const f = numFilters[key] || { op: "gt", value: "" };
    return (
      <NumericFilterContent
        label={getColLabel(key)}
        value={f.value ?? ""}
        op={f.op || "gt"}
        onValueChange={(v) => updateNumFilter(key, "value", v)}
        onClear={() => clearFilter(key)}
        onToggleOp={() => toggleOpDropdown(key)}
        opBtnRef={(el) => {
          opBtnRefs.current[key] = el;
        }}
      />
    );
  };

  /* ── Filtering + sort + pagination ── */
  const filteredRows = useMemo(() => rows.filter((row) => {
    for (const col of FIXED_COLS) {
      const v = textFilters[col.key];
      if (
        v &&
        v.trim() &&
        !String(row[col.key] ?? "")
          .toLowerCase()
          .includes(v.trim().toLowerCase())
      )
        return false;
    }
    for (const col of scrollCols) {
      if (
        isNumActive(col.key) &&
        !applyNumFilter(row[col.key], numFilters[col.key])
      )
        return false;
    }
    return true;
  }), [rows, textFilters, numFilters, scrollCols]);

  const getSortColumnLabel = useCallback(
    (key) => FIXED_COLS.find((c) => c.key === key)?.label || key,
    [],
  );

  const getSortValue = useCallback((row, key) => row[key] ?? "", []);

  const handleSort = useCallback((colKey) => {
    setSortKeys((prev) => toggleSortKey(prev, colKey));
  }, []);

  const onThClick = useMemo(() => createSortThClick(handleSort), [handleSort]);

  const sortedRows = useMemo(() => {
    if (sortKeys.length === 0) return filteredRows;
    return [...filteredRows].sort((a, b) =>
      compareBySortKeys(a, b, sortKeys, getSortValue),
    );
  }, [filteredRows, sortKeys, getSortValue]);

  const totalFiltered = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const pageRows = sortedRows.slice(start, start + pageSize);
  const pageNumbers = (() => {
    if (totalPages <= 7)
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = new Set([
      1,
      totalPages,
      safePage,
      safePage - 1,
      safePage + 1,
      safePage - 2,
      safePage + 2,
    ]);
    return [...pages]
      .filter((p) => p >= 1 && p <= totalPages)
      .sort((a, b) => a - b);
  })();

  const hasTableFilters = hasAnyTableFilters(
    hasActiveTextFilters(textFilters),
    hasActiveNumFilters(numFilters),
  );
  const displayTotals = hasTableFilters
    ? visScrollCols.reduce((acc, col) => {
        acc[col.key] = sumRows(filteredRows, (r) => r[col.key]) ?? 0;
        return acc;
      }, {})
    : totals;

  /* ── Fixed col left offsets ── */
  const fixedLeftOf = (ci) =>
    FIXED_COLS.slice(0, ci).reduce((s, c) => s + c.w, 0);

  if (loading) return <Loading />;

  return (
    <>
      <div style={CARD}>
        {/* ── Card header ── */}
        <div style={HEAD}>
          <h2 style={TITLE_S}>{title}</h2>
          <div style={{ position: "relative" }} ref={colPickerRef}>
            <button
              type="button"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                height: 28,
                padding: "0 10px",
                borderRadius: 6,
                border: "1px solid #E5E7EB",
                background: colPickerOpen ? "#F0F4FF" : "#fff",
                color: "#374151",
                fontSize: 12,
                fontWeight: 500,
                cursor: "pointer",
                fontFamily: FONT,
              }}
              onClick={() => setColPickerOpen((p) => !p)}
            >
              <ColumnsIcon /> Columns
            </button>
            {colPickerOpen && (
              <div style={COL_PICKER_DROP}>
                {groups.map((g) => (
                  <div key={g.key}>
                    <div
                      style={{
                        padding: "6px 14px 2px",
                        fontSize: 10,
                        fontWeight: 700,
                        color: "#9CA3AF",
                        textTransform: "uppercase",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {g.label}
                    </div>
                    {scrollCols
                      .filter((c) => c.groupKey === g.key)
                      .map((col) => {
                      const hidden = hiddenCols.has(col.key);
                      return (
                        <label
                          key={col.key}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            padding: "5px 14px 5px 24px",
                            cursor: "pointer",
                            fontSize: 12,
                            color: "#374151",
                            background: "none",
                            userSelect: "none",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#F8F9FF";
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background = "none";
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={!hidden}
                            style={{
                              accentColor: "#4F46E5",
                              width: 13,
                              height: 13,
                            }}
                            onChange={() =>
                              setHiddenCols((prev) => {
                                const next = new Set(prev);
                                if (hidden) next.delete(col.key);
                                else next.add(col.key);
                                return next;
                              })
                            }
                          />
                          {col.label}
                        </label>
                      );
                    })}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div style={CARD_BODY}>
          {/* ── Table ── */}
          <div style={{ overflowX: "auto" }}>
            <table
              style={{
                borderCollapse: "collapse",
                borderSpacing: 0,
                fontFamily: FONT,
                fontSize: 12,
                tableLayout: "fixed",
                width: STICKY_W + visScrollCols.length * 130,
              }}
            >
              <colgroup>
                {FIXED_COLS.map((col) => (
                  <col key={col.key} style={{ width: col.w }} />
                ))}
                {visScrollCols.map((col) => (
                  <col key={col.key} style={{ width: col.w }} />
                ))}
              </colgroup>
              <thead>
                {/* Row 1 – group headers */}
                <tr style={{ height: 32 }}>
                  <th style={thFixedBlankStyle} colSpan={FIXED_COLS.length} />
                  {visGroups.map((g) => {
                    const count = visScrollCols.filter(
                      (c) => c.groupKey === g.key,
                    ).length;
                    return (
                      <th key={g.key} colSpan={count} style={thGroupStyle}>
                        {g.label}
                      </th>
                    );
                  })}
                </tr>
                {/* Row 2 – column headers */}
                <tr style={{ height: 34 }}>
                  {FIXED_COLS.map((col, ci) => (
                    <th
                      key={col.key}
                      style={{
                        ...thColStyle(false, fixedLeftOf(ci), true),
                        minWidth: col.w,
                        width: col.w,
                        cursor: "pointer",
                      }}
                      onClick={onThClick(col.key)}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          justifyContent: "space-between",
                        }}
                      >
                        <WaterfallThLabel
                          label={col.label}
                          colKey={col.key}
                          sortKeys={sortKeys}
                          getLabel={getSortColumnLabel}
                          highlight={openFilterKey === col.key}
                        />
                        <button
                          ref={(el) => {
                            filterBtnRefs.current[col.key] = el;
                          }}
                          data-filter-btn
                          style={filterIconBtn(
                            isTextActive(col.key) || openFilterKey === col.key,
                          )}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleFilter(col.key);
                          }}
                          title="Filter"
                        >
                          <FilterFunnelIcon />
                        </button>
                      </div>
                    </th>
                  ))}
                  {visScrollCols.map((col) => (
                    <th
                      key={col.key}
                      style={{
                        ...thColStyle(true, 0, false),
                        minWidth: col.w,
                        width: col.w,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 4,
                          justifyContent: "flex-end",
                        }}
                      >
                        <span
                          style={{
                            color:
                              openFilterKey === col.key ? "#60A5FA" : undefined,
                          }}
                        >
                          {col.label}
                        </span>
                        <button
                          ref={(el) => {
                            filterBtnRefs.current[col.key] = el;
                          }}
                          data-filter-btn
                          style={filterIconBtn(
                            isNumActive(col.key) || openFilterKey === col.key,
                          )}
                          onClick={() => toggleFilter(col.key)}
                          title="Filter"
                        >
                          <FilterFunnelIcon />
                        </button>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {totals && (
                <tr style={{ height: 36 }}>
                  {FIXED_COLS.map((col, ci) => (
                    <td
                      key={col.key}
                      style={tdTotalStyle(false, fixedLeftOf(ci), true, false)}
                    >
                      {ci === 0 ? (
                        <strong>{totals.contractName ?? "Total"}</strong>
                      ) : (
                        ""
                      )}
                    </td>
                  ))}
                  {visScrollCols.map((col) => {
                    const v = displayTotals?.[col.key];
                    return (
                      <td
                        key={col.key}
                        style={tdTotalStyle(
                          true,
                          0,
                          false,
                          v != null && Number(v) < 0,
                        )}
                      >
                        {formatCell(v, isPct, col.isVariance, true)}
                      </td>
                    );
                  })}
                </tr>
                )}
                {/* Data rows */}
                {pageRows.map((row, i) => {
                  const bg = i % 2 === 0 ? "#FFFFFF" : "#F7FAFC";
                  return (
                    <tr key={`${row.contractId}-${i}`} style={{ height: 36 }}>
                      {FIXED_COLS.map((col, ci) => (
                        <td
                          key={col.key}
                          style={tdStyle(
                            bg,
                            false,
                            fixedLeftOf(ci),
                            true,
                            false,
                          )}
                        >
                          {ci === 0 ? (
                            <span style={{ fontWeight: 600, color: "#111827" }}>
                              {row.contractId}
                            </span>
                          ) : (
                            row[col.key]
                          )}
                        </td>
                      ))}
                      {visScrollCols.map((col) => {
                        const v = row[col.key];
                        return (
                          <td
                            key={col.key}
                            style={tdStyle(
                              bg,
                              true,
                              0,
                              false,
                              v != null && Number(v) < 0,
                            )}
                          >
                            {formatCell(v, isPct, col.isVariance, false)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
                {pageRows.length === 0 && (
                  <tr>
                    <td
                      colSpan={FIXED_COLS.length + visScrollCols.length}
                      style={{
                        padding: "24px 20px",
                        textAlign: "center",
                        color: "#9CA3AF",
                        fontSize: 13,
                        fontFamily: FONT,
                      }}
                    >
                      No rows match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Pagination footer ── */}
          <div style={paginationBar}>
            <span style={paginationInfo}>
              {totalFiltered === 0
                ? "No rows match the current filters"
                : `${start + 1}–${Math.min(start + pageSize, totalFiltered)} of ${totalFiltered} rows`}
            </span>
            <div style={paginationRight}>
              <span style={pageSizeLabel}>Rows Per Page</span>
              <select
                style={pageSizeSelect}
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                {[10, 25, 50, 100].map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              <div style={paginationControls}>
                <button
                  style={pgBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage(1)}
                  title="First"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M9 10L6 7l3-3M5 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pgBtn(safePage === 1)}
                  disabled={safePage === 1}
                  onClick={() => setPage((p) => p - 1)}
                  title="Previous"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M8.5 10.5L5 7l3.5-3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                {pageNumbers.map((p, i) => (
                  <React.Fragment key={p}>
                    {i > 0 && pageNumbers[i - 1] < p - 1 && (
                      <span style={pgDots}>…</span>
                    )}
                    <button
                      style={p === safePage ? pgBtnActive : pgBtn(false)}
                      onClick={() => setPage(p)}
                    >
                      {p}
                    </button>
                  </React.Fragment>
                ))}
                <button
                  style={pgBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage((p) => p + 1)}
                  title="Next"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5.5 3.5L9 7l-3.5 3.5"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <button
                  style={pgBtn(safePage === totalPages)}
                  disabled={safePage === totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Last"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path
                      d="M5 10l3-3-3-3M9 10V4"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filter panel portal ── */}
      {openFilterKey &&
        createPortal(
          <div
            data-filter-panel
            style={{
              ...fpBase,
              top: filterPanelPos.top,
              left: filterPanelPos.left,
            }}
          >
            {renderFilterContent(openFilterKey)}
          </div>,
          document.body,
        )}

      {/* ── Operator dropdown portal ── */}
      {openOpKey &&
        createPortal(
          <OpDropdownContent
            activeOp={numFilters[openOpKey]?.op || "gt"}
            onSelect={(op) => selectOp(openOpKey, op)}
            style={{ top: opDropdownPos.top, left: opDropdownPos.left }}
          />,
          document.body,
        )}
    </>
  );
}

/* ══════════════════════════════════════════════════════════════ */
/*  Main export                                                   */
/* ══════════════════════════════════════════════════════════════ */
export default function SummaryVsBudget() {
  const classes = useStyles();
  const plClasses = usePlByProgramStyles();
  useReportFonts();
  const history = useHistory();
  const { tab: tabParam } = useParams();
  const activeTab = TABS.find((t) => t.key === tabParam)?.key ?? TABS[0].key;
  const pageTitle = TABS.find((t) => t.key === activeTab)?.label ?? TABS[0].label;

  const [filterOpen, setFilterOpen] = useState(true);
  const [appliedFilters, setAppliedFilters] = useState(initFilterValues);
  const [draftFilters, setDraftFilters] = useState(initFilterValues);
  const [downloading, setDownloading] = useState(false);

  const [versions, setVersions] = useState([]);
  const [versionsLoading, setVersionsLoading] = useState(true);
  const [headerMeta, setHeaderMeta] = useState(null);
  const monthTouchedRef = useRef(false);
  const [month, setMonth] = useState(() =>
    defaultMonthAbbrForYear(String(new Date().getFullYear())),
  );
  const [valueKey, setValueKey] = useState(VALUE_OPTS[0].value);
  const [versionFrom, setVersionFrom] = useState("");
  const [versionTo, setVersionTo] = useState("");

  const currentYear =
    appliedFilters.year && String(appliedFilters.year) !== "All"
      ? String(appliedFilters.year)
      : String(new Date().getFullYear());

  const { dropdownOptions, relationshipRows, effectiveFilters } = useExpandedAppliedFilters(
    appliedFilters,
    draftFilters,
    TAB_FILTERS_KEYS,
  );

  const prerequisitesReady =
    !versionsLoading &&
    relationshipRows !== null &&
    !!versionFrom &&
    !!versionTo;

  useEffect(() => {
    setVersionsLoading(true);
    getVersionsAPI(currentYear)
      .then((res) => {
        const list = Array.isArray(res?.data) ? res.data : [];
        setVersions(list);
        const defaults = resolveDefaultVersions(list);
        setVersionFrom((prev) =>
          prev && list.some((v) => v.key === prev) ? prev : defaults.from,
        );
        setVersionTo((prev) =>
          prev && list.some((v) => v.key === prev) ? prev : defaults.to,
        );
      })
      .finally(() => setVersionsLoading(false));
  }, [currentYear]);

  useEffect(() => {
    monthTouchedRef.current = false;
    setMonth(defaultMonthAbbrForYear(currentYear));
  }, [currentYear]);

  const handleTableMeta = useCallback((meta) => {
    setHeaderMeta(meta);
    if (!monthTouchedRef.current) {
      const abbr = monthAbbrFromActualThrough(meta?.actualThroughMonth);
      if (abbr) setMonth(abbr);
    }
  }, []);

  const tableParams = useMemo(
    () => ({
      month,
      value: valueKey,
      version: versionFrom,
      toVersion: versionTo,
    }),
    [month, valueKey, versionFrom, versionTo],
  );

  const headerAccent = useMemo(() => {
    const from = headerMeta?.fromVersion?.name ?? "Live EAC";
    const to = headerMeta?.toVersion?.name ?? "Budget";
    return `${from} · ${to} · Variance`;
  }, [headerMeta]);

  const headerDate = useMemo(() => {
    const year = new Date().getFullYear();
    const m = Number(headerMeta?.actualThroughMonth);
    if (!m || m < 1 || m > 12) return undefined;
    return `Actuals through ${MONTH_OPTS[m - 1]?.label} ${year}`;
  }, [headerMeta]);

  useEffect(() => {
    if (filterOpen) {
      setDraftFilters(
        pruneInvalidCascadeSelections(
          appliedFilters,
          relationshipRows,
          TAB_FILTERS_KEYS,
        ),
      );
    }
  }, [filterOpen, appliedFilters, relationshipRows]);

  const filterCount = getActiveFilterCount(appliedFilters, TAB_FILTERS_KEYS);

  const activeChips = FILTERS_CONFIG.filter((f) =>
    TAB_FILTERS_KEYS.includes(f.key),
  ).filter((f) => isFilterActive(f, appliedFilters[f.key]));

  const clearChip = useCallback((f) => {
    setAppliedFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
    setDraftFilters((prev) => ({ ...prev, [f.key]: getDefaultValue(f) }));
  }, []);

  const clearAllApplied = useCallback(() => {
    const reset = (prev) => {
      const next = { ...prev };
      TAB_FILTERS_KEYS.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    };
    setAppliedFilters(reset);
    setDraftFilters(reset);
  }, []);

  const handleDraftChange = useCallback(
    (key, value) => {
      setDraftFilters((prev) =>
        applyCascadeDraftChange(
          prev,
          key,
          value,
          relationshipRows,
          TAB_FILTERS_KEYS,
        ),
      );
    },
    [relationshipRows],
  );

  const handleApply = useCallback(() => {
    setAppliedFilters(
      pruneInvalidCascadeSelections(
        draftFilters,
        relationshipRows,
        TAB_FILTERS_KEYS,
      ),
    );
    setFilterOpen(false);
  }, [draftFilters, relationshipRows]);

  const clearAll = useCallback(() => {
    setDraftFilters((prev) => {
      const next = { ...prev };
      TAB_FILTERS_KEYS.forEach((k) => {
        const f = FILTERS_CONFIG.find((fc) => fc.key === k);
        if (f) next[k] = getDefaultValue(f);
      });
      return next;
    });
  }, []);

  const handleDownload = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const api =
        activeTab === "variances_by_contract_l2"
          ? downloadVariancesByContractL2API
          : downloadVariancesByContractAPI;
      const downloadFilters = buildVariancesByContractFilters(
        effectiveFilters,
        tableParams?.month,
      );
      // from/to are the version dropdowns' displayed labels, sent alongside
      // the existing version/toVersion keys (not in place of them).
      const fromName =
        versions.find((v) => v.key === tableParams.version)?.name ??
        tableParams.version;
      const toName =
        versions.find((v) => v.key === tableParams.toVersion)?.name ??
        tableParams.toVersion;
      const { blob, filename } = await api(downloadFilters, {
        version: tableParams.version,
        toVersion: tableParams.toVersion,
        value: tableParams.value,
        from: fromName,
        to: toName,
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        filename ||
        (activeTab === "variances_by_contract_l2"
          ? "variances-by-contract-l2.xlsx"
          : "variances-by-contract.xlsx");
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Variances by Contract download failed:", err);
    } finally {
      setDownloading(false);
    }
  }, [activeTab, effectiveFilters, tableParams, downloading, versions]);

  return (
    <div className={classes.page}>
      {downloading && <Loading />}
      {/* ── Top bar ── */}
      <div className={classes.topBar}>
        <BackButton
          onClick={() => goToReportsCatalog(history)}
          ariaLabel="Back to Reports"
        />
        <nav className={classes.crumb}>
          <span
            className={classes.crumbLink}
            onClick={() => goToReportsCatalog(history)}
          >
            Reports
          </span>
          <span className={classes.crumbSep}>&gt;</span>
          <span className={classes.crumbCur}>Variances by Contract</span>
        </nav>
        <div className={classes.topActions}>
          <button
            type="button"
            className={`${classes.actionBtn} ${classes.actionBtnIconOnly} ${classes.actionBtnActive}`}
            title="Download as Excel"
            aria-label="Download Report"
            onClick={handleDownload}
            disabled={downloading}
          >
            <DownloadIcon />
          </button>
          <button
            type="button"
            className={`${classes.actionBtn} ${filterOpen || filterCount > 0 ? classes.actionBtnActive : ""}`}
            onClick={() => setFilterOpen((p) => !p)}
          >
            <FilterIcon />
            Filters
            {filterCount > 0 && (
              <span className={classes.filterBadge}>{filterCount}</span>
            )}
          </button>
        </div>
      </div>

      {/* ── Tab bar ── */}
      <div className={classes.tabBar}>
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${classes.tab} ${activeTab === tab.key ? classes.tabActive : ""}`}
            onClick={() => history.push(`/projects/reports/summary_vs_budget/${tab.key}`)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Horizontal filter bar ── */}
      {filterOpen && (
        <HorizontalFilterBar
          visibleKeys={TAB_FILTERS_KEYS}
          draft={draftFilters}
          dropdownOptions={dropdownOptions}
          onChange={handleDraftChange}
          onClearAll={clearAll}
          onApply={handleApply}
          applyDisabled={areVisibleFilterValuesEqual(draftFilters, appliedFilters, TAB_FILTERS_KEYS)}
          onClose={() => setFilterOpen(false)}
          classes={classes}
          clearAllDisabled={isOnlyDefaultYear(draftFilters, TAB_FILTERS_KEYS)}
        />
      )}

      {/* ── Active filter chips ── */}
      {!filterOpen && activeChips.length > 0 && (
        <div className={classes.chipsBar}>
          {activeChips.map((f) => {
            const val = appliedFilters[f.key];
            const tip =
              f.type === "multiselect" && Array.isArray(val) && val.length > 1
                ? `${f.label}: ${val.join(", ")}`
                : `${f.label}: ${getDisplayValue(f, val)}`;
            return (
              <span key={f.key} className={classes.chip} title={tip}>
                <span className={classes.chipLabel}>{f.label}:</span>
                {getDisplayValue(f, val)}
                <button
                  className={classes.chipClear}
                  onClick={() => clearChip(f)}
                  title={`Clear ${f.label}`}
                  disabled={
                    f.key === "year" &&
                    String(val) === String(new Date().getFullYear())
                  }
                >
                  ✕
                </button>
              </span>
            );
          })}
          <button
            className={classes.chipsClearAll}
            onClick={clearAllApplied}
            disabled={isOnlyDefaultYear(appliedFilters, TAB_FILTERS_KEYS)}
          >
            Clear All
          </button>
        </div>
      )}

      {/* ── Hero header ── */}
      <div className={classes.headerContainer}>
        <PlPageHeader
          classes={plClasses}
          eyebrow="Contract & Budget"
          title={pageTitle}
          accent={headerAccent}
          badge="Live Forecast"
          date={headerDate}
        />
      </div>

      {/* ── Body ── */}
      <div className={classes.body}>
        <div className={classes.content}>
          {!prerequisitesReady ? (
            <Loading />
          ) : (
            <>
          <div className={classes.filterRow}>
            <div className={classes.filterGroup}>
              <span className={classes.filterLabel}>Month</span>
              <select
                className={classes.filterSelect}
                value={month}
                onChange={(e) => {
                  monthTouchedRef.current = true;
                  setMonth(e.target.value);
                }}
              >
                {MONTH_OPTS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={classes.filterGroup}>
              <span className={classes.filterLabel}>Value</span>
              <select
                className={classes.filterSelect}
                value={valueKey}
                onChange={(e) => setValueKey(e.target.value)}
              >
                {VALUE_OPTS.map((v) => (
                  <option key={v.value} value={v.value}>
                    {v.label}
                  </option>
                ))}
              </select>
            </div>
            <div className={classes.filterGroup}>
              <span className={classes.filterLabel}>From</span>
              <select
                className={classes.filterSelect}
                value={versionFrom}
                onChange={(e) => setVersionFrom(e.target.value)}
                disabled={versions.length === 0}
              >
                {versions.length === 0 && <option value="">—</option>}
                {versions.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={classes.filterGroup}>
              <span className={classes.filterLabel}>To</span>
              <select
                className={classes.filterSelect}
                value={versionTo}
                onChange={(e) => setVersionTo(e.target.value)}
                disabled={versions.length === 0}
              >
                {versions.length === 0 && <option value="">—</option>}
                {versions.map((v) => (
                  <option key={v.key} value={v.key}>
                    {v.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <SummaryTable
            filters={effectiveFilters}
            tableParams={tableParams}
            prerequisitesReady={prerequisitesReady}
            onMeta={handleTableMeta}
            title={pageTitle}
            apiFn={activeTab === "variances_by_contract_l2" ? getVariancesByContractL2API : getVariancesByContractAPI}
            isL2={activeTab === "variances_by_contract_l2"}
          />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
